import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import {
  ProductReportResponse,
  ProductReportItem,
} from 'src/models/Management.interface';
import { getCompanyInfoForPDF, CompanyInfoData } from 'src/utils/company.utils';
import Logo from 'src/assets/images/Logo.png';
import dayjs from 'dayjs';

const colors = {
  primary: '#1a3c5e',
  textDark: '#1a1a2e',
  textMedium: '#555555',
  textLight: '#888888',
  border: '#e0e0e0',
  borderLight: '#f0f0f0',
  backgroundGray: '#f7f8fa',
  white: '#ffffff',
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingBottom: 20,
  },
  companyInfo: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 9,
    color: colors.textMedium,
    marginBottom: 2,
  },
  reportTitleContainer: {
    alignItems: 'flex-end',
  },
  reportTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 4,
  },
  reportDate: {
    fontSize: 10,
    color: colors.textMedium,
  },
  filterSection: {
    marginBottom: 20,
    padding: 12,
    backgroundColor: colors.backgroundGray,
    borderRadius: 8,
  },
  filterTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  filterGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  filterItem: {
    width: '33%',
    marginBottom: 4,
  },
  filterLabel: {
    fontSize: 8,
    color: colors.textLight,
  },
  filterValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.textDark,
  },
  summarySection: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 20,
    marginBottom: 24,
  },
  statCard: {
    width: '30%',
    padding: 10,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.borderLight,
    borderRadius: 8,
    alignItems: 'flex-end',
  },
  statLabel: {
    fontSize: 7,
    color: colors.textLight,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.textDark,
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    padding: 8,
  },
  tableHeaderCell: {
    color: colors.white,
    fontSize: 8,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
    padding: 8,
  },
  tableCell: {
    fontSize: 8,
    color: colors.textDark,
  },
  cellName: { width: '60%' },
  cellProduction: { width: '20%', textAlign: 'right' },
  cellSales: { width: '20%', textAlign: 'right' },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 8,
    color: colors.textLight,
  },
  poweredBy: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 40,
    height: 12,
    marginLeft: 4,
  },
});

interface ProductReportPDFProps {
  data: ProductReportResponse;
  filters: {
    startDate: string | null;
    endDate: string | null;
  };
  companyInfo?: CompanyInfoData;
}

const ProductReportDocument: React.FC<ProductReportPDFProps> = ({
  data,
  filters,
  companyInfo,
}) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              {companyInfo?.companyName || 'OrderX Management'}
            </Text>
            <Text style={styles.companyDetails}>
              {companyInfo?.addressLine1 || ''}
            </Text>
            <Text style={styles.companyDetails}>
              {companyInfo?.city ? `${companyInfo.city}, ` : ''}
              {companyInfo?.postCode || ''}
            </Text>
            <Text style={styles.companyDetails}>
              {companyInfo?.email || ''}
            </Text>
          </View>
          <View style={styles.reportTitleContainer}>
            <Text style={styles.reportTitle}>Product Report</Text>
            <Text style={styles.reportDate}>
              Generated: {dayjs().format('DD MMM YYYY, HH:mm')}
            </Text>
          </View>
        </View>

        <View style={styles.filterSection}>
          <Text style={styles.filterTitle}>Report Parameters</Text>
          <View style={styles.filterGrid}>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Date Range</Text>
              <Text style={styles.filterValue}>
                {filters.startDate
                  ? dayjs(filters.startDate).format('DD/MM/YYYY')
                  : 'All'}{' '}
                -{' '}
                {filters.endDate
                  ? dayjs(filters.endDate).format('DD/MM/YYYY')
                  : 'All'}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.summarySection}>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Total Net Production</Text>
            <Text style={styles.statValue}>{data.summary.totalProduction}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Total Net Sales</Text>
            <Text style={styles.statValue}>{data.summary.totalSales}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.cellName]}>
              Product Name
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellProduction]}>
              Net Production
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellSales]}>
              Net Sales
            </Text>
          </View>

          {data.products.map((product: ProductReportItem, index: number) => (
            <View
              key={product.id}
              style={[
                styles.tableRow,
                { backgroundColor: index % 2 === 0 ? '#fff' : '#fcfcfc' },
              ]}
            >
              <Text style={[styles.tableCell, styles.cellName]}>
                {product.name}
              </Text>
              <Text style={[styles.tableCell, styles.cellProduction]}>
                {product.netProduction}
              </Text>
              <Text style={[styles.tableCell, styles.cellSales]}>
                {product.netSales}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.footer} fixed>
          <View style={styles.poweredBy}>
            <Text style={styles.footerText}>POWERED BY</Text>
            <Image style={styles.logo} src={Logo} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const generateProductReportPDF = async (
  data: ProductReportResponse,
  filters: {
    startDate: string | null;
    endDate: string | null;
  }
): Promise<void> => {
  try {
    const companyInfo = await getCompanyInfoForPDF();

    const blob = await pdf(
      <ProductReportDocument
        data={data}
        filters={filters}
        companyInfo={companyInfo}
      />
    ).toBlob();

    const fileName = `ProductReport-${dayjs().format('YYYYMMDD-HHmm')}.pdf`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Error generating Product Report PDF:', error);
    throw error;
  }
};
